import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getPortfolioReport } from '@/lib/api';
import { BarChart3, Download, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';

export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await getPortfolioReport();
        setReport(data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleExport = () => {
    if (!report) return;
    const csv = [
      'Property,Total Vendors,Compliant,Compliance Rate',
      ...report.properties.map(p => `${p.property_name},${p.total_vendors},${p.compliant_vendors},${p.compliance_rate}%`)
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'portfolio_report.csv'; a.click();
    toast.success('Report exported');
  };

  if (loading) {
    return <div className="space-y-6"><Skeleton className="h-10 w-48" /><Skeleton className="h-80 rounded-xl" /></div>;
  }

  const chartData = report?.properties?.map(p => ({
    name: p.property_name.length > 20 ? p.property_name.slice(0, 20) + '...' : p.property_name,
    rate: p.compliance_rate
  })) || [];

  const getBarColor = (rate) => {
    if (rate >= 80) return 'hsl(142, 56%, 36%)';
    if (rate >= 50) return 'hsl(36, 90%, 50%)';
    return 'hsl(0, 72%, 52%)';
  };

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Reports</h1>
          <p className="text-sm text-muted-foreground">Portfolio compliance reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="reports-export-csv-button" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <Tabs defaultValue="portfolio">
        <TabsList>
          <TabsTrigger value="portfolio">Portfolio Report</TabsTrigger>
          <TabsTrigger value="properties">By Property</TabsTrigger>
        </TabsList>

        <TabsContent value="portfolio" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: 'Compliance Rate', value: `${report?.compliance_rate || 0}%` },
              { label: 'Compliant', value: report?.status_breakdown?.compliant || 0 },
              { label: 'Warning', value: report?.status_breakdown?.warning || 0 },
              { label: 'Expired', value: report?.status_breakdown?.expired || 0 }
            ].map((item, i) => (
              <Card key={i} className="rounded-xl">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{item.label}</p>
                  <p className="text-2xl font-semibold mt-1 tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{item.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Chart */}
          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Compliance by Property</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <RTooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px', fontSize: 12
                      }}
                    />
                    <Bar dataKey="rate" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, i) => <Cell key={i} fill={getBarColor(entry.rate)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl bg-muted/30 border-dashed">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{report?.summary}</p>
              <p className="text-xs text-muted-foreground mt-1">Generated: {report?.generated_at ? new Date(report.generated_at).toLocaleString() : ''}</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="properties">
          <Card className="rounded-xl">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Total Vendors</TableHead>
                    <TableHead>Compliant</TableHead>
                    <TableHead>Compliance Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report?.properties?.map((p, i) => (
                    <TableRow key={i} className="hover:bg-muted/50 transition-colors duration-150">
                      <TableCell className="font-medium text-sm">{p.property_name}</TableCell>
                      <TableCell className="text-sm tabular-nums">{p.total_vendors}</TableCell>
                      <TableCell className="text-sm tabular-nums">{p.compliant_vendors}</TableCell>
                      <TableCell>
                        <Badge variant={p.compliance_rate >= 80 ? 'default' : p.compliance_rate >= 50 ? 'secondary' : 'destructive'} className="text-xs">
                          {p.compliance_rate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
